import { useCallback, useEffect, useState } from "react";
import { getApiBase } from "../apiBase";

const API_BASE = getApiBase();

function formatDateTimeLabel(value, locale = "en") {
  if (!value) {
    return locale === "fr" ? "Pas encore" : "Not yet";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return locale === "fr" ? "Pas encore" : "Not yet";
  }
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatMoneyAmount(amountCents, currency, locale = "en") {
  if (amountCents == null || !Number.isFinite(Number(amountCents))) {
    return "—";
  }
  const code = (currency || "CAD").trim().toUpperCase() || "CAD";
  try {
    return new Intl.NumberFormat(locale === "fr" ? "fr-CA" : "en-CA", {
      style: "currency",
      currency: code,
    }).format(Number(amountCents) / 100);
  } catch {
    return `${(Number(amountCents) / 100).toFixed(2)} ${code}`;
  }
}

export function PaymentHistoryPanel({ authToken, locale = "en", copy }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/billing/payment-history`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadError);
      }
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setItems([]);
      setError(e.message || copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [authToken, copy.loadError]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="app-page app-profile-page app-payment-history-page">
      <div className="payment-history-page-stack">
        <section
          className="profile-section profile-payments-section"
          aria-labelledby="payment-history-page-heading"
        >
          <header>
            <h2 id="payment-history-page-heading">{copy.title}</h2>
            <p>{copy.body}</p>
          </header>

          <div className="payment-history-toolbar">
            <button
              type="button"
              className="secondary profile-discard-btn"
              onClick={() => load()}
              disabled={loading}
            >
              {loading ? copy.refreshing : copy.refresh}
            </button>
          </div>

          {loading ? (
            <p className="profile-page-lead" role="status">
              {copy.loading}
            </p>
          ) : error ? (
            <p className="profile-field-hint" role="status">
              {error}
            </p>
          ) : items.length > 0 ? (
            <ul className="profile-payment-list">
              {items.map((row) => (
                <li key={row.id || row.paymentSortKey} className="profile-payment-item">
                  <div className="profile-payment-item-main">
                    <strong>{row.description || copy.fallbackPayment}</strong>
                    <p>{formatDateTimeLabel(row.createdAt, locale)}</p>
                    {row.provider ? (
                      <p className="profile-field-hint">
                        {String(row.provider).replace(/_/g, " ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="profile-payment-item-meta">
                    <span className="profile-payment-item-amount">
                      {formatMoneyAmount(row.amountCents, row.currency, locale)}
                    </span>
                    {row.status ? (
                      <span className="profile-payment-item-status">{row.status}</span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="profile-field-hint">{copy.emptyState}</p>
          )}
        </section>
      </div>
    </div>
  );
}
